/**
 * Settings Menu Component
 * Unified settings menu that includes:
 * - Sound settings (music and sound effects)
 * - Window mode (fullscreen vs windowed with title bar for Electron)
 * - Logout button
 */

import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useMusic } from '../music/MusicContext';
import { playSoundEffect } from '../hooks/useSoundEffects';
import './SettingsMenu.css';

function SettingsMenu({ onLogout, onResign = null }) {
  const { t } = useTranslation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [displayMode, setDisplayModeState] = useState('fullscreen'); // 'fullscreen' | 'windowed'
  const [isElectron, setIsElectron] = useState(false);
  const buttonRef = useRef(null);
  const menuRef = useRef(null);

  const {
    musicEnabled,
    musicVolume,
    soundsEnabled,
    soundsVolume,
    masterVolume,
    toggleMusic,
    toggleSounds,
    toggleAll,
    setMusicVolume,
    setSoundsVolume,
    setMasterVolume
  } = useMusic();

  // Check if running in Electron and set up fullscreen + display mode
  useEffect(() => {
    const electron = window.electron;
    if (electron && electron.window) {
      setIsElectron(true);

      electron.window.getDisplayMode().then((mode) => {
        setDisplayModeState(mode === 'windowed' ? 'windowed' : 'fullscreen');
      });

      // Get initial fullscreen state
      electron.window.isFullscreen().then((fullscreen) => {
        setIsFullscreen(fullscreen);
      });

      // Listen for fullscreen state changes
      electron.window.onFullscreenChange((fullscreen) => {
        setIsFullscreen(fullscreen);
      });

      // Cleanup listener on unmount
      return () => {
        if (electron && electron.window) {
          electron.window.removeFullscreenListener();
        }
      };
    }
  }, []);

  // Close menu when clicking outside
  useEffect(() => {
    if (!isMenuOpen) return;

    const handleClickOutside = (event) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target) &&
        buttonRef &&
        !buttonRef.current?.contains(event.target)
      ) {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMenuOpen]);

  const handleClick = () => {
    playSoundEffect('/sounds/click.mp3');
    setIsMenuOpen(prev => !prev);
  };

  const handleClose = () => {
    setIsMenuOpen(false);
  };

  const handleFullscreenToggle = () => {
    const electron = window.electron;
    if (electron && electron.window) {
      electron.window.toggleFullscreen();
    }
  };

  const handleSetDisplayMode = (mode) => {
    if (mode === displayMode) return;
    const electron = window.electron;
    if (!electron || !electron.window || !electron.window.setDisplayMode) return;
    playSoundEffect('/sounds/click.mp3');
    electron.window.setDisplayMode(mode);
    setDisplayModeState(mode);
    // Window will recreate and reload; menu will unmount
  };

  const handleExit = () => {
    playSoundEffect('/sounds/click.mp3');
    handleClose();
    const electron = window.electron;
    if (electron && electron.quitApp) {
      electron.quitApp();
    }
  };

  const handleLogout = () => {
    playSoundEffect('/sounds/click.mp3');
    handleClose();
    if (onLogout) {
      onLogout();
    }
  };

  const handleResign = () => {
    if (typeof onResign !== 'function') {
      throw new Error('SettingsMenu: onResign is required when resign button is shown');
    }
    playSoundEffect('/sounds/click.mp3');
    handleClose();
    onResign();
  };

  // Determine icon state: muted if both are disabled, otherwise show enabled icon
  const isMuted = !musicEnabled && !soundsEnabled;

  return (
    <div 
      ref={buttonRef}
      className="settings-menu-container"
      style={{ position: 'relative', marginRight: '10px' }}
    >
      <div
        onClick={handleClick}
        title={t('settings.open', 'Open settings')}
        style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}
      >
        <svg 
          width="24" 
          height="24" 
          viewBox="0 0 24 24" 
          fill="none" 
          xmlns="http://www.w3.org/2000/svg"
          style={{ filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))' }}
        >
          <path 
            d="M19.14 12.94c.04-.3.06-.61.06-.94 0-.32-.02-.64-.07-.94l2.03-1.58c.18-.14.23-.41.12-.61l-1.92-3.32c-.12-.22-.37-.29-.59-.22l-2.39.96c-.5-.38-1.03-.7-1.62-.94L14.4 2.81c-.04-.24-.24-.41-.48-.41h-3.84c-.24 0-.43.17-.47.41l-.36 2.54c-.59.24-1.13.57-1.62.94l-2.39-.96c-.22-.08-.47 0-.59.22L2.74 8.87c-.12.21-.08.47.12.61l2.03 1.58c-.05.3-.07.62-.07.94s.02.64.07.94l-2.03 1.58c-.18.14-.23.41-.12.61l1.92 3.32c.12.22.37.29.59.22l2.39-.96c.5.38 1.03.7 1.62.94l.36 2.54c.05.24.24.41.48.41h3.84c.24 0 .44-.17.47-.41l.36-2.54c.59-.24 1.13-.56 1.62-.94l2.39.96c.22.08.47 0 .59-.22l1.92-3.32c.12-.22.07-.47-.12-.61l-2.01-1.58zM12 15.6c-1.98 0-3.6-1.62-3.6-3.6s1.62-3.6 3.6-3.6 3.6 1.62 3.6 3.6-1.62 3.6-3.6 3.6z" 
            fill="white"
          />
        </svg>
      </div>

      {isMenuOpen && (
        <div className="settings-menu" ref={menuRef}>
          <button 
            className="settings-menu-close"
            onClick={handleClose}
            aria-label={t('common.close', 'Close')}
          >
            ×
          </button>

          <div className="settings-menu-content">
            {/* Sound Settings Section */}
            <div className="settings-menu-section">
              <div className="settings-menu-section-title">
                {t('settings.sound', 'Sound')}
              </div>

              {/* Music Section */}
              <div className="settings-menu-subsection">
                <div className="settings-menu-subsection-header">
                  <label className="settings-menu-toggle">
                    <input
                      type="checkbox"
                      checked={musicEnabled}
                      onChange={toggleMusic}
                    />
                    <span>{t('soundSettings.music', 'Music')}</span>
                  </label>
                </div>
                {musicEnabled && (
                  <div className="settings-menu-volume">
                    <label>
                      {t('soundSettings.volume', 'Volume')}: {Math.round(musicVolume * 100)}%
                    </label>
                    <input
                      type="range"
                      min="0"
                      max="1"
                      step="0.01"
                      value={musicVolume}
                      onChange={(e) => setMusicVolume(parseFloat(e.target.value))}
                      className="settings-menu-slider"
                    />
                  </div>
                )}
              </div>

              {/* Sound Effects Section */}
              <div className="settings-menu-subsection">
                <div className="settings-menu-subsection-header">
                  <label className="settings-menu-toggle">
                    <input
                      type="checkbox"
                      checked={soundsEnabled}
                      onChange={toggleSounds}
                    />
                    <span>{t('soundSettings.sounds', 'Sound Effects')}</span>
                  </label>
                </div>
                {soundsEnabled && (
                  <div className="settings-menu-volume">
                    <label>
                      {t('soundSettings.volume', 'Volume')}: {Math.round(soundsVolume * 100)}%
                    </label>
                    <input
                      type="range"
                      min="0"
                      max="1"
                      step="0.01"
                      value={soundsVolume}
                      onChange={(e) => setSoundsVolume(parseFloat(e.target.value))}
                      onMouseUp={(e) => {
                        const currentVolume = parseFloat(e.target.value);
                        if (soundsEnabled && currentVolume > 0 && masterVolume > 0) {
                          try {
                            const audio = new Audio('/sounds/damage.mp3');
                            audio.volume = Math.min(1, masterVolume * currentVolume);
                            audio.play().catch(err => {
                              console.debug('Sound effect play failed:', err);
                            });
                          } catch (error) {
                            console.debug('Sound effect error:', error);
                          }
                        }
                      }}
                      onTouchEnd={(e) => {
                        const currentVolume = parseFloat(e.target.value);
                        if (soundsEnabled && currentVolume > 0 && masterVolume > 0) {
                          try {
                            const audio = new Audio('/sounds/damage.mp3');
                            audio.volume = Math.min(1, masterVolume * currentVolume);
                            audio.play().catch(err => {
                              console.debug('Sound effect play failed:', err);
                            });
                          } catch (error) {
                            console.debug('Sound effect error:', error);
                          }
                        }
                      }}
                      className="settings-menu-slider"
                    />
                  </div>
                )}
              </div>

              {/* Master Toggle + master output level */}
              <div className="settings-menu-subsection">
                <div className="settings-menu-subsection-header">
                  <label className="settings-menu-toggle">
                    <input
                      type="checkbox"
                      checked={musicEnabled && soundsEnabled}
                      onChange={toggleAll}
                    />
                    <span>{t('soundSettings.all', 'All Sounds')}</span>
                  </label>
                </div>
                <div className="settings-menu-volume">
                  <label>
                    {t('soundSettings.masterVolume', 'Master volume')}: {Math.round(masterVolume * 100)}%
                  </label>
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.01"
                    value={masterVolume}
                    onChange={(e) => setMasterVolume(parseFloat(e.target.value))}
                    onMouseUp={(e) => {
                      const m = parseFloat(e.target.value);
                      if (!soundsEnabled || soundsVolume <= 0 || m <= 0) return;
                      try {
                        const audio = new Audio('/sounds/damage.mp3');
                        audio.volume = Math.min(1, m * soundsVolume);
                        audio.play().catch(err => {
                          console.debug('Sound effect play failed:', err);
                        });
                      } catch (error) {
                        console.debug('Sound effect error:', error);
                      }
                    }}
                    onTouchEnd={(e) => {
                      const m = parseFloat(e.target.value);
                      if (!soundsEnabled || soundsVolume <= 0 || m <= 0) return;
                      try {
                        const audio = new Audio('/sounds/damage.mp3');
                        audio.volume = Math.min(1, m * soundsVolume);
                        audio.play().catch(err => {
                          console.debug('Sound effect play failed:', err);
                        });
                      } catch (error) {
                        console.debug('Sound effect error:', error);
                      }
                    }}
                    className="settings-menu-slider"
                  />
                </div>
              </div>
            </div>

            {/* Window Mode Section (Electron only) */}
            {isElectron && (
              <div className="settings-menu-section">
                <div className="settings-menu-section-title">
                  {t('settings.windowMode', 'Window Mode')}
                </div>
                <div className="settings-menu-display-mode-row">
                  <button
                    className={`settings-menu-button settings-menu-mode-btn ${displayMode === 'fullscreen' ? 'settings-menu-mode-active' : ''}`}
                    onClick={() => handleSetDisplayMode('fullscreen')}
                    title={t('settings.fullscreenModeDesc', 'Full screen, no title bar')}
                  >
                    {t('settings.fullscreenMode', 'Fullscreen')}
                  </button>
                  <button
                    className={`settings-menu-button settings-menu-mode-btn ${displayMode === 'windowed' ? 'settings-menu-mode-active' : ''}`}
                    onClick={() => handleSetDisplayMode('windowed')}
                    title={t('settings.windowedModeDesc', 'Window with title bar – drag to move')}
                  >
                    {t('settings.windowedMode', 'Windowed')}
                  </button>
                </div>
                {displayMode === 'windowed' && (
                  <button
                    className="settings-menu-button"
                    onClick={handleFullscreenToggle}
                  >
                    {isFullscreen
                      ? t('settings.exitFullscreen', 'Exit Fullscreen (F11)')
                      : t('settings.enterFullscreen', 'Enter Fullscreen (F11)')
                    }
                  </button>
                )}
                <button
                  className="settings-menu-button settings-menu-button-exit"
                  onClick={handleExit}
                >
                  {t('settings.exit', 'Exit')}
                </button>
              </div>
            )}

            {/* Logout Section */}
            <div className="settings-menu-section">
              {typeof onResign === 'function' ? (
                <button
                  type="button"
                  className="settings-menu-button settings-menu-button-resign"
                  onClick={handleResign}
                >
                  {t('battleUI.resign', 'Resign')}
                </button>
              ) : null}
              <button
                type="button"
                className="settings-menu-button settings-menu-button-logout"
                onClick={handleLogout}
              >
                {t('common.logout', 'Logout')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SettingsMenu;
